import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import * as api from '../services/api';
import type { BrandingData, BrandConfigData } from '../services/api';

interface BrandingContextValue {
  branding: BrandingData;
  brandConfig: BrandConfigData | null;
  loading: boolean;
  reload: () => Promise<void>;
  loadBrand: (slug: string) => Promise<void>;
  /** #2372 — put back the HOST's own brand (reseller host: its brand; platform host: the platform branding). */
  resetBranding: () => Promise<void>;
}

const DEFAULT_BRANDING: BrandingData = {
  logoUrl: null,
  primaryColor: '#e8b25a',
  secondaryColor: '#d4943a',
};

const BrandingContext = createContext<BrandingContextValue>({
  branding: DEFAULT_BRANDING,
  brandConfig: null,
  loading: true,
  reload: async () => {},
  loadBrand: async () => {},
  resetBranding: async () => {},
});

export function useBranding() {
  return useContext(BrandingContext);
}

/**
 * #2318 — the "GP" tile fallback, reused wherever a page shows a brand initial next to the
 * brand name (Dashboard, PaymentLinkLanding). Mirrors CheckoutPage's own inline brandInitials
 * (not switched to call this helper — its exact source text is pinned by
 * checkout-brand-header.test.ts).
 */
export function brandInitials(companyName: string | null | undefined): string {
  return companyName
    ? companyName.trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join('').toUpperCase()
    : 'GP';
}

/**
 * #2370 — every #2318 call site resolved the displayed brand name as `brandConfig?.companyName ||
 * 'GigaPress AI'`, which treats ANY brand_configs row (including the platform's own /gigapress,
 * /creative813, /try-now pages — brand_configs rows with no partner) as a reseller. Only a brand
 * that actually belongs to a reseller partner (brandConfig.isReseller, set by the backend from
 * partner_id IS NOT NULL) should ever replace the product name. Everyone else — no brand, a
 * platform brand, or isReseller missing/false — keeps the product name exactly as before #2318.
 */
export const PLATFORM_DISPLAY_NAME = 'GigaPress AI';

/** The reseller's own company name when the brand belongs to a reseller partner; null for no brand or a
 *  platform brand. For call sites with their own platform default copy (e.g. the domain-hold notice's
 *  "reserved for you on GigaPress"), which must receive no brand at all on the platform. */
export function resellerBrandName(cfg: Pick<BrandConfigData, 'companyName' | 'isReseller'> | null | undefined): string | null {
  const name = cfg?.isReseller ? (cfg.companyName || '').trim() : '';
  return name || null;
}

export function brandDisplayName(cfg: Pick<BrandConfigData, 'companyName' | 'isReseller'> | null | undefined): string {
  return resellerBrandName(cfg) || PLATFORM_DISPLAY_NAME;
}

export function brandTileInitials(cfg: Pick<BrandConfigData, 'companyName' | 'isReseller'> | null | undefined): string {
  return cfg?.isReseller ? brandInitials(cfg.companyName) : 'GP';
}

// Generate a 10-step shade palette from a single hex color
function hexToHsl(hex: string): [number, number, number] {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0, s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
    }
  }
  return [Math.round(h * 360), Math.round(s * 100), Math.round(l * 100)];
}

function hslToHex(h: number, s: number, l: number): string {
  const s1 = s / 100, l1 = l / 100;
  const c = (1 - Math.abs(2 * l1 - 1)) * s1;
  const x = c * (1 - Math.abs((h / 60) % 2 - 1));
  const m = l1 - c / 2;
  let r = 0, g = 0, b = 0;
  if (h < 60) { r = c; g = x; }
  else if (h < 120) { r = x; g = c; }
  else if (h < 180) { g = c; b = x; }
  else if (h < 240) { g = x; b = c; }
  else if (h < 300) { r = x; b = c; }
  else { r = c; b = x; }
  const toHex = (v: number) => Math.round((v + m) * 255).toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

function generatePalette(hex: string): Record<string, string> {
  const [h, s] = hexToHsl(hex);
  const lightnesses = [96, 92, 85, 74, 62, 52, 43, 35, 27, 20];
  const keys = ['50', '100', '200', '300', '400', '500', '600', '700', '800', '900'];
  const palette: Record<string, string> = {};
  for (let i = 0; i < keys.length; i++) {
    palette[keys[i]] = hslToHex(h, Math.min(s + 5, 100), lightnesses[i]);
  }
  return palette;
}

function hexToRgb(hex: string): [number, number, number] {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return [r, g, b];
}

/** WCAG 2 relative luminance of an sRGB colour (0-255 channels). */
function luminance(rgb: [number, number, number]): number {
  const [r, g, b] = rgb.map((c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

function contrastRatio(a: [number, number, number], b: [number, number, number]): number {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
}

/**
 * --ac-t: the accent as small TEXT on its own 12% tint (--ac-d) — badges, chips, selected tabs. The raw accent
 * can fail WCAG AA there (GigaPress blue #1a8fe3 on its tint over a card is 4.22:1), so lighten it just enough
 * to reach 4.6:1 against the tint over the lightest dark surface (#1c1c22, cards). Accents that already pass
 * (the default amber: ~7:1) come back unchanged; the brand colour itself (--ac) is never altered (2026-09-30).
 */
export function readableAccentText(primary: [number, number, number]): [number, number, number] {
  const surface: [number, number, number] = [28, 28, 34];
  const tint = primary.map((c, i) => c * 0.12 + surface[i] * 0.88) as [number, number, number];
  for (let step = 0; step <= 20; step++) {
    const w = step / 20;
    const t = primary.map((c) => Math.round(c + (255 - c) * w)) as [number, number, number];
    if (contrastRatio(t, tint) >= 4.6) return t;
  }
  return [255, 255, 255];
}

/**
 * --ac-on: the text colour that sits ON a solid accent fill (the floating "Get Help" pill). White there is
 * 1.9:1 on the default amber and 3.4:1 on GigaPress blue — both fail WCAG AA for 14px text (#325, 2026-10-12)
 * — while a dark partner brand needs white. Pick whichever of the app's dark ink (--bg) or white reads better
 * on THIS brand's accent; a mid-tone accent where neither reaches 4.5:1 still gets the better of the two.
 */
export function textOnAccent(primary: [number, number, number]): string {
  return contrastRatio([16, 16, 20], primary) >= contrastRatio([255, 255, 255], primary) ? '#101014' : '#ffffff';
}

function applyBrandingCSS(branding: BrandingData) {
  const palette = generatePalette(branding.primaryColor);
  const root = document.documentElement;

  // Set brand palette (backward compat)
  for (const [key, val] of Object.entries(palette)) {
    root.style.setProperty(`--color-brand-${key}`, val);
  }

  // Set design system accent from primary color
  const primary = branding.primaryColor || '#e8b25a';
  const [r, g, b] = hexToRgb(primary);
  root.style.setProperty('--ac', primary);
  const [tr, tg, tb] = readableAccentText([r, g, b]);
  root.style.setProperty('--ac-t', `rgb(${tr},${tg},${tb})`);
  root.style.setProperty('--ac-on', textOnAccent([r, g, b]));
  root.style.setProperty('--ac-d', `rgba(${r},${g},${b},.12)`);
  root.style.setProperty('--ac-g', `rgba(${r},${g},${b},.06)`);
  root.style.setProperty('--bdr-a', `rgba(${r},${g},${b},.4)`);
}

/**
 * #2318 — the HOST's own brand. An explicit brandSlug prop wins. Else, on a white-label reseller host, the host-header
 * middleware injects window.SF_PARTNER into every SPA response (see CheckoutPage's own identical read). The provider is
 * mounted ONCE at the app root with no brandSlug prop, so without that fallback brandConfig stayed null everywhere
 * except CheckoutPage's own local resolution, and Dashboard/LoginPage/PaymentLinkLanding never saw the partner's brand.
 * Undefined = the platform host.
 */
export function hostBrandSlug(
  brandSlugProp: string | null | undefined,
  sfPartner: { brandSlug?: string | null } | null | undefined,
): string | undefined {
  return brandSlugProp || sfPartner?.brandSlug || undefined;
}

type BrandingResponse<T> = { success: boolean; data?: T };

/**
 * The provider's loaders, outside React so their ORDER is testable (brand-reset-2372.test.ts).
 *
 * #2372 (m29) — a /<brand>/checkout loads that brand into this app-wide provider. reset() puts the HOST's brand back
 * when the customer leaves. On the platform host that means clearing brandConfig too, which carries the reseller's
 * name into brandDisplayName on every page. Each call is numbered and only the LATEST applies its result: a slow brand
 * fetch that resolves after the customer left must not re-apply that brand over the host's.
 */
export function createBrandingLoader(deps: {
  getBranding: () => Promise<BrandingResponse<BrandingData>>;
  getBrand: (slug: string) => Promise<BrandingResponse<BrandConfigData>>;
  setBranding: (branding: BrandingData) => void;
  setBrandConfig: (config: BrandConfigData | null) => void;
  setLoading: (loading: boolean) => void;
  applyCss: (branding: BrandingData) => void;
}) {
  let latest = 0;

  function applyDefault() {
    deps.setBranding(DEFAULT_BRANDING);
    deps.applyCss(DEFAULT_BRANDING);
  }

  async function load() {
    const call = ++latest;
    try {
      const res = await deps.getBranding();
      if (call !== latest) return;
      if (res.success && res.data) {
        deps.setBranding(res.data);
        deps.applyCss(res.data);
      } else {
        applyDefault();
      }
    } catch {
      if (call === latest) applyDefault();
    } finally {
      deps.setLoading(false);
    }
  }

  async function loadBrand(slug: string) {
    const call = ++latest;
    try {
      const res = await deps.getBrand(slug);
      if (call !== latest) return;
      if (res.success && res.data) {
        deps.setBrandConfig(res.data);
        const brandBranding: BrandingData = {
          logoUrl: res.data.logoUrl || null,
          primaryColor: res.data.primaryColor,
          secondaryColor: res.data.secondaryColor,
        };
        deps.setBranding(brandBranding);
        deps.applyCss(brandBranding);
      }
    } catch {
      // Keep the current branding
    }
  }

  async function reset(hostSlug: string | undefined) {
    if (hostSlug) return loadBrand(hostSlug);
    deps.setBrandConfig(null);
    return load();
  }

  return { load, loadBrand, reset };
}

export function BrandingProvider({ children, brandSlug }: { children: React.ReactNode; brandSlug?: string }) {
  const [branding, setBranding] = useState<BrandingData>(DEFAULT_BRANDING);
  const [brandConfig, setBrandConfig] = useState<BrandConfigData | null>(null);
  const [loading, setLoading] = useState(true);
  const loaderRef = useRef<ReturnType<typeof createBrandingLoader> | null>(null);
  if (!loaderRef.current) {
    loaderRef.current = createBrandingLoader({
      getBranding: () => api.getBranding(),
      getBrand: (slug) => api.getBrand(slug),
      setBranding,
      setBrandConfig,
      setLoading,
      applyCss: applyBrandingCSS,
    });
  }
  const loader = loaderRef.current;

  const resetBranding = () => loader.reset(hostBrandSlug(brandSlug, (window as any).SF_PARTNER));

  useEffect(() => {
    void resetBranding();
  }, [brandSlug]);

  return (
    <BrandingContext.Provider value={{ branding, brandConfig, loading, reload: loader.load, loadBrand: loader.loadBrand, resetBranding }}>
      {children}
    </BrandingContext.Provider>
  );
}
